import type { ChipProps } from "@mui/material";
import type {
  SecurityEvent,
  SecurityOutcome,
  SecuritySeverity,
  SecurityTotals,
} from "@xop/dashboard-shared";
import { isAnomalousAccess } from "@xop/dashboard-shared";

/**
 * Pure formatting/data helpers + shared constants for the Security console,
 * extracted from `SecurityPanel.tsx` so they unit-test directly.
 */

export const REFRESH_MS = 30_000;

export const ZERO_TOTALS: SecurityTotals = {
  total: 0,
  bySeverity: { info: 0, warn: 0, critical: 0 },
  byOutcome: { denied: 0, failed: 0, success: 0 },
  bySource: {},
  anomalies: 0,
};

export const OUTCOME_LABELS: Record<SecurityOutcome, string> = {
  denied: "Denied",
  failed: "Failed",
  success: "Successful",
};

export type Tone = "danger" | "warn" | "good";

/** Map a severity to a MUI Chip colour (critical→error, warn→warning, info→default).
 * Returns a concrete colour (never `undefined`) so it can be passed to Chip
 * `color` under exactOptionalPropertyTypes. */
export function severityColor(s: SecuritySeverity): NonNullable<ChipProps["color"]> {
  if (s === "critical") return "error";
  if (s === "warn") return "warning";
  return "default";
}

/** SQLite `datetime('now')` is space-separated UTC with no zone. Normalize
 * to an ISO instant so the browser renders it in the operator's locale. */
export function formatTs(ts: string): { local: string; utc: string } {
  const iso = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  const d = new Date(iso);
  const local = Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
  return { local, utc: `${ts} UTC` };
}

export function detailSummary(detail: SecurityEvent["detail"]): string {
  if (!detail) return "";
  const parts: string[] = [];
  for (const key of [
    "code",
    "method",
    "path",
    "required_permission",
    "granted_via",
    "reason",
  ]) {
    const v = (detail as Record<string, unknown>)[key];
    if (v != null && v !== "") parts.push(`${key}=${String(v)}`);
  }
  // Fall back to a compact JSON for anything not in the common set.
  if (parts.length === 0) {
    try {
      return JSON.stringify(detail);
    } catch {
      return "";
    }
  }
  return parts.join("  ");
}

/** Fallback when an (older) response omits `totals` — derive what we can from
 * the loaded page so the console still renders instead of going blank. */
export function fallbackTotals(events: SecurityEvent[]): SecurityTotals {
  const t: SecurityTotals = {
    total: events.length,
    bySeverity: { info: 0, warn: 0, critical: 0 },
    byOutcome: { denied: 0, failed: 0, success: 0 },
    bySource: {},
    anomalies: 0,
  };
  for (const e of events) {
    t.bySeverity[e.severity] += 1;
    t.byOutcome[e.outcome] += 1;
    t.bySource[e.source] = (t.bySource[e.source] ?? 0) + 1;
    if (isAnomalousAccess(e)) t.anomalies += 1;
  }
  return t;
}

export function windowLabel(hours: number): string {
  return hours >= 24 ? `${hours / 24}d` : `${hours}h`;
}

/** Tile value colour per tone (was the text-[color:var(--color-…)] set). */
export function toneColor(tone: Tone | undefined, value: number): string {
  if (tone === "danger" && value > 0) return "error.main";
  if (tone === "warn" && value > 0) return "warning.main";
  if (tone === "good" && value > 0) return "success.main";
  return "text.primary";
}
